// Where a product stands in the review workflow, as a pill: the dialog header
// shows it whatever tab is open, and the Product Review tab shows the same one
// beside the product it belongs to.
import React from "react"
import { ArrowTopRightOnSquareIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import { buildContentUrl } from "../../../Storefront/utils/contentRoutes"
import { EPublishStatus, publishStateTexts } from "./editor-config"
import { ATTENTION_PILL } from "../../../AdminShared/ui"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

// the admin theme's own colours: a state reads the same as every other
// warning / success / error in the admin, and follows the theme when it changes
const style = {
  [EPublishStatus.Draft]: "border-admin_bordered text-admin_text_secondary bg-admin_neutral_1/40",
  [EPublishStatus.InReview]: "border-admin_warning text-admin_warning bg-admin_warning/10",
  [EPublishStatus.Published]: "border-admin_success text-admin_success bg-admin_success/10",
  [EPublishStatus.Rejected]: "border-admin_error text-admin_error bg-admin_error/10",
  [EPublishStatus.ReviewRequired]: ATTENTION_PILL,
}

const icon = {
  [EPublishStatus.InReview]: ClockIcon,
  [EPublishStatus.Published]: CheckCircleIcon,
  [EPublishStatus.Rejected]: XCircleIcon,
  [EPublishStatus.ReviewRequired]: ExclamationTriangleIcon,
}

/**
 * `product` is the catalog document: an approved one has a page in the store,
 * and the chip beside the state opens it. `onClick` makes the state a button
 * (the Product Review tab), `badge` goes inside it (what waits there), and
 * `children`, when given, stand in the state's place (a live product's changes
 * waiting for review are its state then).
 */
export default function PublishStatePill({ status = EPublishStatus.Draft, product, size = "md", className = "", onClick, badge = null, children = null }) {
  const t = useProductEditorTexts()
  const Icon = icon[status]
  const box = size === "sm" ? "h-[34px] px-3 text-xs" : "h-9 px-4 text-sm"
  const storeUrl = status === EPublishStatus.Published && product?.pid ? buildContentUrl(product) : null
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {children ||
        React.createElement(
          onClick ? "button" : "span",
          {
            ...(onClick ? { type: "button", onClick, title: t`Product Review` } : {}),
            className: `inline-flex items-center gap-2 rounded-md border font-medium whitespace-nowrap ${box} ${style[status] || style[EPublishStatus.Draft]} ${onClick ? "hover:opacity-80 transition-opacity" : ""}`,
            "data-testid": "publish-state",
          },
          Icon && <Icon className="w-4 h-4" />,
          t(publishStateTexts[status] ?? publishStateTexts[0]),
          badge
        )}
      {storeUrl && (
        <a
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          title={t`Open this product's page in the store`}
          className={`inline-flex items-center gap-1.5 rounded-md border border-admin_bordered bg-admin_paper font-medium whitespace-nowrap text-admin_primary hover:border-admin_primary transition-colors ${box}`}
          data-testid="view-in-store"
        >
          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          {t`View in store`}
        </a>
      )}
    </span>
  )
}
